import {
    Component,
    ChangeDetectionStrategy,
    inject,
    computed,
    Input,
    OnChanges,
    SimpleChanges,
    signal,
} from '@angular/core'
import { Router } from '@angular/router'
import { forkJoin, of } from 'rxjs'
import { catchError, map, switchMap, take } from 'rxjs/operators'
import { SalesFunnelService } from '@core/services/sales-funnel/sales-funnel.service'
import { SalesFunnelHelper } from '../../../../core/helpers/sales-funnel.helper'
import { valueConstants } from '../../../../core/constants/valueConstants'
import { ICON_PATHS } from '../../../../core/constants/assets.constants'
import {
    LEAD_DASHBOARD_QUERY_PARAMS,
    CLIENT_DASHBOARD_QUERY_PARAMS,
    FUNNEL_SOURCE_HOME,
    HOME_DASHBOARD_QUERY_PARAMS,
    TEAM_ASSIGNED_SCHOOLS_DRILLDOWN_STORAGE_PREFIX,
} from '../../../../core/constants/app.constants'
import type {
    ICoreMetricItem,
    ICoreMetricLinkPayload,
} from '../../../../core/models/dashboard.model'
import type { ITeamAssignedSchoolScope } from '@app/features/crm/models/lead.model'
import { AggregatedClientsService } from '@app/features/crm/services/aggregated-clients.service'
import type { IOrganisation, IOrganisationResponse } from '@app/features/crm/models/client.model'
import { EiTooltipDirective } from '../../../../shared/directives'
import { ZardButtonComponent } from '@/styles/components/button'

@Component({
    selector: 'app-core-metrics',
    templateUrl: './core-metrics.component.html',
    styleUrls: ['./core-metrics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EiTooltipDirective, ZardButtonComponent],
})
export class CoreMetricsComponent implements OnChanges {
    private readonly distinctSchoolsMetricLabel = 'Distinct Schools Assigned'
    private readonly TEAM_ASSIGNED_SCHOOLS_COUNT_PAGE_SIZE = 25
    private readonly salesFunnelService = inject(SalesFunnelService)
    private readonly aggregatedClientsService = inject(AggregatedClientsService)
    private readonly router = inject(Router)

    @Input() title: string = valueConstants.CORE_METRICS_TITLE
    /** True when rendered in ZM Team View on Home page. */
    @Input() isTeamView = false
    readonly infoIcon = ICON_PATHS.INFO

    readonly metricSkeletonItems = [1, 2, 3, 4, 5, 6, 7, 8, 9]
    readonly isLoading = this.salesFunnelService.isLoading
    readonly error = this.salesFunnelService.error
    readonly stageMetrics = this.salesFunnelService.stageMetrics
    readonly keyMetrics = this.salesFunnelService.keyMetrics
    private readonly teamAssignedSchoolsCount = signal<number | null>(null)

    readonly metrics = computed(() => {
        const stages = this.stageMetrics()
        if (!stages.length) {
            return []
        }
        const newSchoolsWonCount = this.salesFunnelService.newSchoolsWonCount()
        const leadStatusMetrics = this.salesFunnelService.leadStatusMetrics()
        const keyMetrics = this.keyMetrics()
        const metrics = SalesFunnelHelper.transformToCoreMetrics(
            stages,
            newSchoolsWonCount,
            leadStatusMetrics,
            keyMetrics,
            this.isTeamView,
        )
        const teamAssignedSchoolsCount = this.teamAssignedSchoolsCount()
        if (this.isTeamView && teamAssignedSchoolsCount !== null) {
            return metrics.map((metric) =>
                metric.label === this.distinctSchoolsMetricLabel
                    ? { ...metric, value: teamAssignedSchoolsCount.toString() }
                    : metric,
            )
        }
        return metrics
    })

    readonly hasError = computed(() => {
        return !!this.error()
    })

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isTeamView']) {
            if (this.isTeamView) {
                this.loadTeamAssignedSchoolsCount()
            } else {
                this.teamAssignedSchoolsCount.set(null)
            }
        }
    }

    isMetricClickable(metric: ICoreMetricItem): boolean {
        return (
            !!metric.linkPayload?.ids?.length ||
            (this.isTeamView && metric.label === this.distinctSchoolsMetricLabel)
        )
    }

    onMetricClick(metric: ICoreMetricItem): void {
        const linkPayload = metric.linkPayload
        if (this.shouldResolveTeamAssignedSchools(metric)) {
            this.salesFunnelService
                .resolveTeamAssignedSchoolScopes()
                .pipe(take(1))
                .subscribe((scopes) => {
                    if (!scopes.length) {
                        return
                    }
                    const drilldownKey = this.storeTeamAssignedSchoolsDrilldown(scopes)
                    if (!drilldownKey) {
                        return
                    }
                    this.navigateToClientDashboard([], true, true, drilldownKey)
                })
            return
        }

        if (!linkPayload?.ids?.length) {
            return
        }
        if (linkPayload.type === 'lead') {
            this.navigateToLeadDashboard(linkPayload)
        } else {
            this.navigateToClientDashboard(linkPayload.ids, this.isTeamView)
        }
    }

    private shouldResolveTeamAssignedSchools(metric: ICoreMetricItem): boolean {
        return this.isTeamView && metric.label === this.distinctSchoolsMetricLabel
    }

    private navigateToLeadDashboard(linkPayload: ICoreMetricLinkPayload): void {
        const queryParams: Record<string, string> = {
            [LEAD_DASHBOARD_QUERY_PARAMS.LEAD_IDS]: linkPayload.ids.join(','),
            [LEAD_DASHBOARD_QUERY_PARAMS.SOURCE]: FUNNEL_SOURCE_HOME,
        }
        if (this.isTeamView) {
            queryParams[HOME_DASHBOARD_QUERY_PARAMS.TAB] = valueConstants.TEAM_VIEW_QUERY_PARAM
        }
        this.router.navigate(['/crm/lead-dashboard'], {
            queryParams,
        })
    }

    private navigateToClientDashboard(
        clientIds: number[],
        isTeamView: boolean,
        shouldLoadAll = false,
        drilldownKey?: string,
    ): void {
        const queryParams: Record<string, string> = {
            [CLIENT_DASHBOARD_QUERY_PARAMS.SOURCE]: FUNNEL_SOURCE_HOME,
        }
        if (clientIds.length) {
            queryParams[CLIENT_DASHBOARD_QUERY_PARAMS.CLIENT_IDS] = clientIds.join(',')
        }
        if (drilldownKey) {
            queryParams[CLIENT_DASHBOARD_QUERY_PARAMS.DRILLDOWN_KEY] = drilldownKey
        }
        if (shouldLoadAll) {
            queryParams[CLIENT_DASHBOARD_QUERY_PARAMS.LOAD_ALL] = 'true'
        }
        if (isTeamView) {
            queryParams[HOME_DASHBOARD_QUERY_PARAMS.TAB] = valueConstants.TEAM_VIEW_QUERY_PARAM
            queryParams[CLIENT_DASHBOARD_QUERY_PARAMS.VIEW] = valueConstants.TEAM_VIEW_QUERY_PARAM
        }
        this.router.navigate(['/crm/dashboard'], {
            queryParams,
        })
    }

    private storeTeamAssignedSchoolsDrilldown(scopes: ITeamAssignedSchoolScope[]): string | null {
        try {
            const drilldownKey = `${TEAM_ASSIGNED_SCHOOLS_DRILLDOWN_STORAGE_PREFIX}_${Date.now()}`
            sessionStorage.setItem(drilldownKey, JSON.stringify(scopes))
            return drilldownKey
        } catch {
            return null
        }
    }

    private loadTeamAssignedSchoolsCount(): void {
        this.salesFunnelService
            .resolveTeamAssignedSchoolScopes()
            .pipe(
                take(1),
                switchMap((scopes) => {
                    if (!scopes.length) {
                        return of(0)
                    }
                    return forkJoin(
                        scopes.map((scope) => this.loadAllAssignedOrganisationsForScope(scope)),
                    ).pipe(
                        map((organisationGroups) => {
                            const organisationCodes = new Set<number>()
                            organisationGroups
                                .flat()
                                .forEach((organisation) =>
                                    organisationCodes.add(organisation.organisation_code),
                                )
                            return organisationCodes.size
                        }),
                    )
                }),
                catchError(() => of(null)),
            )
            .subscribe((count) => {
                this.teamAssignedSchoolsCount.set(count)
            })
    }

    private loadAllAssignedOrganisationsForScope(scope: ITeamAssignedSchoolScope) {
        const pageSize = this.TEAM_ASSIGNED_SCHOOLS_COUNT_PAGE_SIZE
        return this.aggregatedClientsService
            .getClientsForUser(
                scope.userId,
                undefined,
                valueConstants.DEFAULT_PAGE,
                pageSize,
                undefined,
                undefined,
                valueConstants.TEAM_VIEW_QUERY_PARAM,
            )
            .pipe(
                switchMap((firstResponse) => {
                    const totalPages =
                        firstResponse.pagination.total_pages ??
                        Math.ceil((firstResponse.pagination.total ?? 0) / pageSize)

                    if (totalPages <= 1) {
                        return of(firstResponse.data || [])
                    }

                    const remainingRequests = Array.from(
                        { length: totalPages - 1 },
                        (_, index) => index + 2,
                    ).map((page) =>
                        this.aggregatedClientsService
                            .getClientsForUser(
                                scope.userId,
                                undefined,
                                page,
                                pageSize,
                                undefined,
                                undefined,
                                valueConstants.TEAM_VIEW_QUERY_PARAM,
                            )
                            .pipe(
                                catchError(() =>
                                    of(this.createEmptyOrganisationResponse(page, pageSize)),
                                ),
                            ),
                    )

                    return forkJoin(remainingRequests).pipe(
                        map((responses) => [
                            ...(firstResponse.data || []),
                            ...responses.flatMap((response) => response.data || []),
                        ]),
                    )
                }),
                catchError(() => of([] as IOrganisation[])),
            )
    }

    private createEmptyOrganisationResponse(page: number, limit: number): IOrganisationResponse {
        return {
            success: true,
            message: '',
            data: [],
            pagination: {
                page,
                limit,
                total: 0,
                total_pages: 1,
            },
        }
    }
}
